/**
 * Shared field-mapping helpers for the public-share-view roster row adapters
 * (`CreatorsTab/publicRosterRowAdapter.ts`, `ConfirmedTab.publicRosterRowAdapter.ts`,
 * `ScriptsTab.publicRosterRowAdapter.ts` — and whichever of Content/
 * Live-Content's adapters (Tasks 19-20) end up needing the same shape).
 *
 * Task 17's review flagged Creators' and Confirmed's adapters as ~15-18 of
 * ~25 lines verbatim-identical (id/user_id synthesis, the nested `platform`
 * object, `snapshot_metrics`, `deliverables`) with only `status` and a
 * handful of tab-specific fields genuinely diverging. Task 18 (Scripts) is a
 * third near-identical adapter, so this module extracts exactly the overlap
 * instead of copying it a third time.
 *
 * It does NOT try to unify `status` or any production-phase field (posts/
 * addons, script/content-specific placeholders) — those diverge meaningfully
 * per tab (see each adapter's own doc comment: Confirmed freezes `status` to
 * a literal because it's always true for that tab; Scripts can't do the same
 * because the wire never carries the per-row script-review status at all).
 * Forcing those into this module would need more branching than it saves.
 *
 * `snapshot_metrics` / `deliverables` are split into their own functions
 * (not folded into the identity one) because `RosterEntryScript` — the
 * Scripts-tab target type — has NEITHER field at all: the wire's
 * `followers`/`avg_views`/`engagement_rate`/`deliverables` are real Creators/
 * Confirmed-only surface, not something every roster row type carries.
 */
import type { PublicRosterRow } from '@discovr/features';
import { DELIVERABLE_REGISTRY } from '@discovr/types';

/** `id`/`user_id` synthesized from `username` — unique per platform per
 *  campaign (the roster domain never has two rows for the same username on
 *  the same campaign/platform; the decoy `PublicRosterTable` in
 *  `publicShellViews.tsx` already relies on this same assumption for its own
 *  `getRowId={(row) => row.username}`), plus the nested `platform` shape
 *  every authed roster row type carries. Every adapter's target row needs
 *  exactly this identity sub-shape, regardless of what else diverges. */
export interface CommonAdaptedIdentityFields {
  id: string;
  user_id: string;
  platform: {
    username: string;
    name: string;
    profile_picture_url: string;
    is_verified: boolean;
  };
}

export function adaptPublicRosterRowIdentity(row: PublicRosterRow): CommonAdaptedIdentityFields {
  return {
    id: row.username,
    user_id: row.username,
    platform: {
      username: row.username,
      name: row.name ?? '',
      profile_picture_url: row.avatar_url ?? '',
      is_verified: false,
    },
  };
}

/** `snapshot_metrics` — shared by Creators and Confirmed only. */
export interface CommonAdaptedSnapshotMetrics {
  snapshot_metrics: {
    followers: number;
    avg_views: number;
    engagement_rate: number;
  };
}

export function adaptPublicRosterRowSnapshotMetrics(
  row: PublicRosterRow,
): CommonAdaptedSnapshotMetrics {
  return {
    snapshot_metrics: {
      followers: row.followers ?? 0,
      avg_views: row.avg_views ?? 0,
      engagement_rate: row.engagement_rate ?? 0,
    },
  };
}

/** `deliverables` — shared by Creators and Confirmed only (same reason as
 *  `snapshot_metrics` above: `RosterEntryScript` has no such field). */
export interface CommonAdaptedDeliverables {
  deliverables: Array<{ type: string; base_price?: number; is_selected: boolean }>;
}

const isMainType = (type: string): boolean => DELIVERABLE_REGISTRY[type]?.category === 'main';

/** Drops selected secondary mains so the public view shows the primary's chip only,
 *  like the signed-in view. Candidates and add-ons stay; an unresolved primary hides nothing. */
export function withoutSecondaryMains<T extends { type: string; is_selected: boolean; is_primary?: boolean }>(
  deliverables: readonly T[],
): T[] {
  const selectedMains = deliverables.filter((d) => d.is_selected && isMainType(d.type));
  const primaries = selectedMains.filter((d) => d.is_primary);
  if (selectedMains.length < 2 || primaries.length !== 1) return [...deliverables];
  return deliverables.filter((d) => !(d.is_selected && isMainType(d.type) && !d.is_primary));
}

export function adaptPublicRosterRowDeliverables(row: PublicRosterRow): CommonAdaptedDeliverables {
  return {
    deliverables: withoutSecondaryMains(row.deliverables ?? []).map((d) => ({
      type: d.type,
      base_price: d.base_price,
      is_selected: d.is_selected,
    })),
  };
}
